/* global Turbo */
import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

/**
 * RuleFormController - Coordinates form interactions
 *
 * Responsibilities:
 * - Listen to model select changes
 * - Tell ConditionBuilder to load fields for selected model
 * - Debounce-refresh the live plain-language summary as the form changes
 * - Disable the branch selector while the rule applies to all branches
 */
export default class extends Controller {
  static outlets = [
    "ui--automation--condition-builder",
    "ui--automation--action-builder",
  ]
  static targets = ["branchScope"]
  static values = {
    previewUrl: { type: String, default: "" },
  }

  connect() {
    this._previewTimeout = null
    this._previewAbort = null

    if (this.previewUrlValue) {
      this.schedulePreview = this.schedulePreview.bind(this)
      // Builders emit custom bubbling events on every change; plain inputs
      // (name, trigger radios, model select) are covered by input/change
      this.element.addEventListener(
        "condition-builder:change",
        this.schedulePreview,
      )
      this.element.addEventListener(
        "action-builder:change",
        this.schedulePreview,
      )
      this.element.addEventListener("input", this.schedulePreview)
      this.element.addEventListener("change", this.schedulePreview)
    }
  }

  disconnect() {
    clearTimeout(this._previewTimeout)
    this._previewAbort?.abort()
    if (this.previewUrlValue) {
      this.element.removeEventListener(
        "condition-builder:change",
        this.schedulePreview,
      )
      this.element.removeEventListener(
        "action-builder:change",
        this.schedulePreview,
      )
      this.element.removeEventListener("input", this.schedulePreview)
      this.element.removeEventListener("change", this.schedulePreview)
    }
  }

  /**
   * When model changes, tell condition builder and action builder to load fields.
   * Fired by Ui::ComboBoxComponent's "ui--combo-box:change" custom event, which
   * carries the selected value in event.detail (there's no native <select> to
   * read event.target.value from).
   */
  modelChanged(event) {
    const modelName = event.detail.value

    if (modelName && this.hasUiAutomationConditionBuilderOutlet) {
      this.uiAutomationConditionBuilderOutlet.loadFieldsForModel(modelName)
    }

    if (modelName && this.hasUiAutomationActionBuilderOutlet) {
      this.uiAutomationActionBuilderOutlet.loadTriggerFields(modelName)
    }
  }

  /**
   * "Applies to all branches" toggled: disable the branch selector's fieldset
   * so none of its inputs are submitted and the linked branches stay as they
   * are (CONEK-7915).
   */
  allBranchesChanged(event) {
    if (event.target.type !== "checkbox" || !this.hasBranchScopeTarget) return

    this.branchScopeTarget.disabled = event.target.checked
  }

  // Copy the live (unsaved) rule field values into the "Save as template"
  // modal's hidden inputs just before it opens. The modal lives outside this
  // form (its own <form>), so we reach it by id rather than a Stimulus target.
  prepareSaveAsTemplate() {
    const modalForm = document.getElementById("save-as-template-form")
    if (!modalForm) return

    const read = (name) =>
      this.element.querySelector(`[name='${name}']`)?.value || ""
    const trigger =
      this.element.querySelector(
        "[name='automation_rule[trigger_event]']:checked",
      )?.value || ""

    const allBranches = this.element.querySelector(
      "input[type='checkbox'][name='automation_rule[applies_to_all_branches]']",
    )?.checked

    const map = {
      target_model: read("automation_rule[target_model]"),
      trigger_event: trigger,
      condition_tree: read("automation_rule[condition_tree]"),
      actions_json: read("automation_rule[actions_json]"),
      metadata_json: read("automation_rule[metadata_json]"),
      applies_to_all_branches: allBranches ? "1" : "0",
    }

    Object.entries(map).forEach(([key, value]) => {
      const field = modalForm.querySelector(`[data-save-template='${key}']`)
      if (field) field.value = value
    })

    // Seed the template name and description from the rule if the user
    // hasn't typed them in the modal
    ;["name", "description"].forEach((attr) => {
      const ruleValue = read(`automation_rule[${attr}]`)
      const field = modalForm.querySelector(`[name='automation_rule[${attr}]']`)
      if (field && !field.value && ruleValue) field.value = ruleValue
    })
  }

  schedulePreview() {
    clearTimeout(this._previewTimeout)
    this._previewTimeout = setTimeout(() => this._refreshPreview(), 600)
  }

  async _refreshPreview() {
    try {
      const body = new FormData(this.element)
      body.delete("_method")

      // Cancel any in-flight preview so a slow earlier request can't resolve
      // after a faster later one and overwrite the summary with stale content.
      this._previewAbort?.abort()
      const abort = new AbortController()
      this._previewAbort = abort

      const response = await post(this.previewUrlValue, {
        body,
        responseKind: "turbo-stream",
        signal: abort.signal,
      })
      if (!abort.signal.aborted && response.ok) {
        Turbo.renderStreamMessage(await response.text)
      }
    } catch (_e) {
      // The summary is advisory — never block the form on network errors
      // (including AbortError from a superseded request)
    }
  }
}
